import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { packagingApi } from 'api/packaging';
import { isValidCreatePackagingItem } from 'components/packaging/components/packagingPOV2/utils/packagingPOValidation';
import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { Button } from 'shadcn/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from 'shadcn/components/ui/Card';
import { Combobox } from 'shadcn/components/ui/Combobox';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import { Textarea } from 'shadcn/components/ui/Textarea';

const labelClassName =
    'text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground leading-tight min-h-[28px]';
const DEFAULT_ITEM = {
    description: '',
    id: '',
    masterSku: '',
    notes: '',
    numPallets: '',
    qty: '',
    unitPrice: '',
    unitsPerCase: '',
    vendorSku: '',
};

const createDraftId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

/**
 * @param {Object} props
 * @param {Array} props.items
 * @param {(items: Array) => void} props.onItemsChange
 * @param {boolean} [props.showValidation]
 * @param {string[]} [props.itemErrors]
 */
export function PackagingPOCreateItemsCard({ itemErrors = [], items, onItemsChange, showValidation = false }) {
    const [packagingOptions, setPackagingOptions] = useState([]);
    const [draftItem, setDraftItem] = useState(DEFAULT_ITEM);
    const [selectedPackagingId, setSelectedPackagingId] = useState('');

    const handleItemsChange = useCallback(
        (nextItems) => {
            if (typeof onItemsChange === 'function') {
                onItemsChange(Array.isArray(nextItems) ? nextItems : []);
            }
        },
        [onItemsChange],
    );

    const handleRowFieldChange = useCallback(
        (uid, field) => (event) => {
            const value = event?.target?.value ?? '';
            const nextItems = (Array.isArray(items) ? items : []).map((item) =>
                item?.uid === uid
                    ? {
                          ...item,
                          [field]: value,
                      }
                    : item,
            );
            handleItemsChange(nextItems);
        },
        [handleItemsChange, items],
    );

    const packagingSelectOptions = useMemo(
        () =>
            packagingOptions.map((option) => ({
                label: option.label,
                value: option.value,
            })),
        [packagingOptions],
    );

    const loadPackaging = useCallback(async () => {
        try {
            const response = await packagingApi.getPackaging();
            if (response?.status === false) {
                toast.error('Unable to load packaging options');
                setPackagingOptions([]);
                return;
            }

            const rows = response?.data?.rows ?? [];
            const nextOptions = rows
                .map((row) => row?.model)
                .filter(Boolean)
                .map((model) => ({
                    label: `SKU ${model.masterSKU}: ${model.description}`,
                    model,
                    value: String(model.id),
                }));
            setPackagingOptions(nextOptions);
        } catch (error) {
            toast.error(error?.message ?? 'Unable to load packaging options');
            setPackagingOptions([]);
        }
    }, []);

    useEffect(() => {
        loadPackaging();
    }, [loadPackaging]);

    const handleItemFieldChange = useCallback(
        (field) => (event) => {
            const value = event?.target?.value ?? '';
            setDraftItem((prev) => ({
                ...prev,
                [field]: value,
            }));
        },
        [],
    );

    const handlePackagingChange = useCallback(
        (value) => {
            const selected = packagingOptions.find((option) => option.value === value)?.model;
            if (!selected) {
                setSelectedPackagingId('');
                setDraftItem((prev) => ({
                    ...prev,
                    description: '',
                    id: '',
                    masterSku: '',
                }));
                return;
            }

            setSelectedPackagingId(value);
            setDraftItem((prev) => ({
                ...prev,
                description: selected.description ?? '',
                id: selected.id ? String(selected.id) : '',
                masterSku: selected.masterSKU ?? '',
                unitsPerCase: prev.unitsPerCase || String(selected.unitsPerCase ?? ''),
            }));
        },
        [packagingOptions],
    );

    const handleAddItem = useCallback(() => {
        if (!isValidCreatePackagingItem(draftItem)) {
            toast.error('All required item fields must be filled.');
            return;
        }

        const nextItem = {
            uid: createDraftId(),
            description: draftItem.description,
            id: Number(draftItem.id),
            masterSku: draftItem.masterSku,
            notes: draftItem.notes,
            numPallets: draftItem.numPallets,
            qty: draftItem.qty,
            unitPrice: draftItem.unitPrice,
            unitsPerCase: draftItem.unitsPerCase,
            unitsPerCaseUOM: 'ct',
            vendorSku: draftItem.vendorSku,
        };
        handleItemsChange([...(Array.isArray(items) ? items : []), nextItem]);
        setDraftItem(DEFAULT_ITEM);
        setSelectedPackagingId('');
    }, [draftItem, handleItemsChange, items]);

    const handleRemoveItem = useCallback(
        (itemToRemove) => {
            const targetUid = itemToRemove?.uid;
            const nextItems = (Array.isArray(items) ? items : []).filter((item) => item?.uid !== targetUid);
            handleItemsChange(nextItems);
        },
        [handleItemsChange, items],
    );

    return (
        <Card>
            <CardHeader className="pb-0">
                <CardTitle>Items</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
                {showValidation && itemErrors.length > 0 ? (
                    <div className="pb-6">
                        <Alert variant="destructive">
                            <AlertTitle>Fix the following items to save this purchase order</AlertTitle>
                            <AlertDescription>
                                <ul className="list-disc pl-5">
                                    {itemErrors.map((message) => (
                                        <li key={message}>{message}</li>
                                    ))}
                                </ul>
                            </AlertDescription>
                        </Alert>
                    </div>
                ) : null}
                <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 xl:grid-cols-12">
                    <div className="flex flex-col gap-2 xl:col-span-4">
                        <Label htmlFor="packagingSku" className={labelClassName}>
                            Packaging SKU <span className="text-destructive">*</span>
                        </Label>
                        <Combobox
                            id="packagingSku"
                            options={packagingSelectOptions}
                            value={selectedPackagingId}
                            handleSelect={handlePackagingChange}
                            placeholder="Select packaging SKU"
                            popoverWidth="trigger"
                            triggerClassName="w-full justify-between"
                            commandProps={{
                                filter: (value, search) => {
                                    const option = packagingSelectOptions.find((opt) => opt.value === value);
                                    if (!option) return false;
                                    return option.label.toLowerCase().includes(search.toLowerCase());
                                },
                            }}
                        />
                    </div>

                    <div className="flex flex-col gap-2 xl:col-span-1">
                        <Label htmlFor="packaging-qty" className={labelClassName}>
                            Quantity (cases) <span className="text-destructive">*</span>
                        </Label>
                        <Input
                            id="packaging-qty"
                            type="number"
                            min="1"
                            className="w-24"
                            value={draftItem.qty}
                            onChange={handleItemFieldChange('qty')}
                        />
                    </div>

                    <div className="flex flex-col gap-2 xl:col-span-1">
                        <Label htmlFor="packaging-unit-price" className={labelClassName}>
                            Unit Price <span className="text-destructive">*</span>
                        </Label>
                        <Input
                            id="packaging-unit-price"
                            type="number"
                            inputMode="decimal"
                            step="0.01"
                            min="0.01"
                            className="w-24"
                            value={draftItem.unitPrice}
                            onChange={handleItemFieldChange('unitPrice')}
                        />
                    </div>

                    <div className="flex flex-col gap-2 xl:col-span-1">
                        <Label htmlFor="packaging-pallets" className={labelClassName}>
                            Pallets per SKU <span className="text-destructive">*</span>
                        </Label>
                        <Input
                            id="packaging-pallets"
                            type="number"
                            min="1"
                            className="w-24"
                            value={draftItem.numPallets}
                            onChange={handleItemFieldChange('numPallets')}
                        />
                    </div>

                    <div className="flex flex-col gap-2 xl:col-span-1">
                        <Label htmlFor="packaging-units-per-case" className={labelClassName}>
                            Units per Case
                        </Label>
                        <Input
                            id="packaging-units-per-case"
                            type="number"
                            min="1"
                            className="w-24"
                            value={draftItem.unitsPerCase}
                            onChange={handleItemFieldChange('unitsPerCase')}
                        />
                    </div>

                    <div className="flex flex-col gap-2 xl:col-span-2">
                        <Label htmlFor="packaging-vendor-sku" className={labelClassName}>
                            Vendor SKU <span className="text-destructive">*</span>
                        </Label>
                        <Input
                            id="packaging-vendor-sku"
                            type="text"
                            value={draftItem.vendorSku}
                            onChange={handleItemFieldChange('vendorSku')}
                        />
                    </div>

                    <div className="flex flex-col gap-2 xl:col-span-4">
                        <Label htmlFor="packaging-notes" className={labelClassName}>
                            Notes
                        </Label>
                        <Textarea
                            id="packaging-notes"
                            value={draftItem.notes}
                            onChange={handleItemFieldChange('notes')}
                            placeholder="Add item notes"
                            rows={3}
                        />
                    </div>
                </div>

                <div className="mt-4 flex justify-end">
                    <Button type="button" size="sm" onClick={handleAddItem}>
                        Add Item
                    </Button>
                </div>

                <div className="mt-6">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Item Master SKU</TableHead>
                                <TableHead>Description</TableHead>
                                <TableHead>Quantity (cases)</TableHead>
                                <TableHead>Unit Price</TableHead>
                                <TableHead>Pallets per SKU</TableHead>
                                <TableHead>Units per Case</TableHead>
                                <TableHead>Vendor SKU</TableHead>
                                <TableHead>Notes</TableHead>
                                <TableHead>Total</TableHead>
                                <TableHead>Remove</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {(Array.isArray(items) ? items : []).length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={10} className="text-sm text-muted-foreground">
                                        No items added yet.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                (Array.isArray(items) ? items : []).map((item) => (
                                    <TableRow key={item.uid ?? `${item.id}-${item.vendorSku}-${item.masterSku}`}>
                                        <TableCell>{item.masterSku}</TableCell>
                                        <TableCell>{item.description}</TableCell>
                                        <TableCell>
                                            <Input
                                                aria-label="Row qty"
                                                type="number"
                                                min="1"
                                                className="w-24"
                                                value={item.qty ?? ''}
                                                onChange={handleRowFieldChange(item.uid, 'qty')}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Input
                                                aria-label="Row unit price"
                                                type="number"
                                                inputMode="decimal"
                                                step="0.01"
                                                min="0.01"
                                                className="w-24"
                                                value={item.unitPrice ?? ''}
                                                onChange={handleRowFieldChange(item.uid, 'unitPrice')}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Input
                                                aria-label="Row pallets per sku"
                                                type="number"
                                                min="1"
                                                className="w-24"
                                                value={item.numPallets ?? ''}
                                                onChange={handleRowFieldChange(item.uid, 'numPallets')}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Input
                                                aria-label="Row units per case"
                                                type="number"
                                                min="1"
                                                className="w-24"
                                                value={item.unitsPerCase ?? ''}
                                                onChange={handleRowFieldChange(item.uid, 'unitsPerCase')}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Input
                                                aria-label="Row vendor sku"
                                                type="text"
                                                className="w-32"
                                                value={item.vendorSku ?? ''}
                                                onChange={handleRowFieldChange(item.uid, 'vendorSku')}
                                            />
                                        </TableCell>
                                        <TableCell className="max-w-[240px]">
                                            <Textarea
                                                aria-label="Row notes"
                                                className="min-h-[32px]"
                                                rows={2}
                                                value={item.notes ?? ''}
                                                onChange={handleRowFieldChange(item.uid, 'notes')}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {item.qty && item.unitPrice
                                                ? `$${(Number(item.qty) * Number(item.unitPrice)).toFixed(2)}`
                                                : ''}
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleRemoveItem(item)}
                                            >
                                                Remove
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    );
}

PackagingPOCreateItemsCard.displayName = 'PackagingPOCreateItemsCard';
